import React, { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import FeatherIcon from '../common/FeatherIcon';
import useLens, { EVERYTHING } from './useLens';
import useSurfaceSelection from './surfaceSelection';
import useDefaultSurface from './defaultSurface';
import { useSurfaces } from '../../config/surfaces';
import NavLock from '../licensing/NavLock';
import { lensName, lensStyle } from './lenses';
import usePillarNav from '../layout/Sidebar/pillarNav';
import './lens.css';

export const LENS_TAB_STORAGE_KEY = 'saf3ai.lens.tab';
const TABS = ['use-case', 'role'];

const readTab = () => {
  try {
    const v = window.localStorage.getItem(LENS_TAB_STORAGE_KEY);
    return TABS.includes(v) ? v : null;
  } catch {
    return null;
  }
};

const writeTab = (tab) => {
  try { window.localStorage.setItem(LENS_TAB_STORAGE_KEY, tab); } catch { /* blocked storage */ }
};

/**
 * Header pill that switches the lens. Self-contained: no props required.
 *
 * Two tabs, the same lenses arranged two ways: by use case, and by role (a role
 * bundles one or two use cases, which its row names). Everything sits below
 * both. The menu opens on the tab last used, else on the current lens's kind,
 * else on roles, the easier first question.
 *
 * Choosing a lens stores it and lands on its home; choosing Everything only
 * restores the full sidebar. A `?lens=<id>` link applies that lens the same way
 * and drops the parameter, so a shared link sets someone up in one click.
 * Below the lenses, Surfaces: a toggle per module section the sidebar
 * shows (it replaced the product dropdown). It stays open while you tick, and
 * is offered on its own to an org with no lenses but more than one surface.
 * With neither, the pill is not shown.
 *
 * Under the "New navigation" switch the menu is personas only: the roles and Everything, no
 * use-case tab and no Surfaces (the pillar sidebar does not draw module sections, so their
 * toggles had nothing to change there). There it offers Default surface instead: one toggle per
 * AI surface, and the chosen one is what every page's Surface filter opens on (see
 * ./defaultSurface), so nobody re-picks a surface page by page. A use-case lens already chosen, or named in a
 * `?lens=` link, still applies and shows on the pill.
 */
const LensSwitcher = ({ className = '' }) => {
  const { t } = useTranslation('nav');
  const navigate = useNavigate();
  const location = useLocation();
  const {
    lens, lensId, setLens, useCases, roles, ready,
  } = useLens();
  const { on: personasOnly } = usePillarNav();
  const surfaceState = useSurfaceSelection();
  const surfaceList = personasOnly ? [] : surfaceState.surfaces;
  const registry = useSurfaces();
  const { surfaceId: defaultSurfaceId, choose: chooseDefaultSurface } = useDefaultSurface();
  const defaultOptions = personasOnly
    ? registry.surfaces.filter((sf) => sf.kind !== 'group').map((sf) => ({ id: sf.id, label: sf.label }))
    : [];
  const defaultChosen = defaultOptions.find((o) => o.id === defaultSurfaceId) || null;
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState(() => readTab() || (lens.kind === 'use-case' ? 'use-case' : 'role'));
  const wrapRef = useRef(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const menuId = useId();
  const tabsId = useId();

  const known = (id) => id === 'all' || [...useCases, ...roles].some((l) => l.id === id);
  const findLens = (id) => [...useCases, ...roles].find((l) => l.id === id) || null;

  // ?lens=<id>: apply once the lenses are known, then drop the parameter.
  const search = location?.search || '';
  useEffect(() => {
    if (!ready || !search) return;
    const params = new URLSearchParams(search);
    const wanted = params.get('lens');
    if (wanted === null) return;
    params.delete('lens');
    const rest = params.toString();
    if (known(wanted)) {
      setLens(wanted);
      const target = findLens(wanted);
      if (target?.kind) {
        setTab(target.kind);
        writeTab(target.kind);
      }
    }
    navigate(`${location.pathname}${rest ? `?${rest}` : ''}${location.hash || ''}`, { replace: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, search]);

  const items = () => (menuRef.current
    ? Array.from(menuRef.current.querySelectorAll('[role="menuitemradio"], [role="menuitemcheckbox"], [role="menuitem"]'))
    : []);

  useEffect(() => {
    if (!open) return undefined;
    const list = items();
    const current = list.find((el) => el.getAttribute('aria-checked') === 'true') || list[0];
    if (current) current.focus();
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tab]);

  const hasLenses = ready && (personasOnly ? roles.length > 0 : (useCases.length > 0 || roles.length > 0));
  const hasSurfaces = surfaceList.length >= 2;
  if (!hasLenses && !hasSurfaces) return null;
  const shownCount = surfaceList.filter((sf) => sf.shown).length;
  const surfaceSummary = t('nav:lens.surfaces.summary', { shown: shownCount, total: surfaceList.length });

  const close = (refocus = true) => {
    setOpen(false);
    if (refocus && buttonRef.current) buttonRef.current.focus();
  };

  const showTab = (next) => {
    setTab(next);
    writeTab(next);
  };

  const choose = (id) => {
    const applied = setLens(id);
    close();
    if (!applied || applied === 'all') return;
    const next = findLens(applied);
    if (next?.home) navigate(next.home);
  };

  const onMenuKeyDown = (e) => {
    const list = items();
    const at = list.indexOf(document.activeElement);
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      list[(at + 1) % list.length]?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      list[(at - 1 + list.length) % list.length]?.focus();
    } else if (!personasOnly && (e.key === 'ArrowRight' || e.key === 'ArrowLeft')) {
      e.preventDefault();
      showTab(tab === 'role' ? 'use-case' : 'role');
    } else if (e.key === 'Home') {
      e.preventDefault();
      list[0]?.focus();
    } else if (e.key === 'End') {
      e.preventDefault();
      list[list.length - 1]?.focus();
    } else if (e.key === 'Tab') {
      setOpen(false);
    }
  };

  const name = hasLenses ? lensName(lens, t) : surfaceSummary;
  const caption = hasLenses ? t('nav:lens.caption') : t('nav:lens.surfaces.title');
  const listed = personasOnly || tab === 'role' ? roles : useCases;

  const row = (l, subtitle) => {
    const checked = l.id === lensId;
    return (
      <button
        key={l.id}
        type="button"
        role="menuitemradio"
        aria-checked={checked}
        tabIndex={-1}
        className={`lens-menu-item${checked ? ' is-checked' : ''}`}
        onClick={() => choose(l.id)}
      >
        <span className={`lens-tile${l.abbr.length > 3 ? ' long' : ''}`} style={{ background: l.color }} aria-hidden="true">{l.abbr}</span>
        <span className="lens-menu-text">
          <span className="lens-menu-name">{lensName(l, t)}</span>
          <span className="lens-menu-blurb">{subtitle}</span>
        </span>
        <span className="lens-menu-check" aria-hidden="true">
          {checked && <FeatherIcon name="check" size={16} />}
        </span>
      </button>
    );
  };

  return (
    <div className={`lens-switcher ${className}`.trim()} ref={wrapRef}>
      <button
        ref={buttonRef}
        type="button"
        className={`lens-switcher-pill${lensId !== 'all' ? ' is-active' : ''}`}
        style={lensStyle(lens)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={hasLenses
          ? t('nav:lens.switcherLabel', { name })
          : `${caption}: ${surfaceSummary}`}
        onClick={() => (open ? close(false) : setOpen(true))}
        onKeyDown={(e) => {
          if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && !open) {
            e.preventDefault();
            setOpen(true);
          }
        }}
      >
        <FeatherIcon name="aperture" size={18} className="lens-switcher-icon" aria-hidden="true" />
        <span className="lens-switcher-caption">{caption}</span>
        <span className="lens-switcher-name">{name}</span>
        {hasLenses && hasSurfaces && surfaceState.customised && (
          <span className="lens-switcher-count">{surfaceSummary}</span>
        )}
        {hasLenses && defaultChosen && (
          <span className="lens-switcher-count">{defaultChosen.label}</span>
        )}
        <FeatherIcon name="chevron-down" size={12} className="lens-switcher-chevron" aria-hidden="true" />
      </button>
      {open && (
        <div className="lens-popover">
          {hasLenses && (
            <>
              <div className="lens-menu-caption" aria-hidden="true">{t('nav:lens.menuCaption')}</div>
              {!personasOnly && <div className="lens-tabs" role="tablist" aria-label={t('nav:lens.tabs.label')} id={tabsId}>
                {TABS.map((key) => (
                  <button
                    key={key}
                    type="button"
                    role="tab"
                    aria-selected={tab === key}
                    aria-controls={menuId}
                    className={`lens-tab${tab === key ? ' is-selected' : ''}`}
                    onClick={() => showTab(key)}
                  >
                    {t(key === 'role' ? 'nav:lens.tabs.role' : 'nav:lens.tabs.useCase')}
                  </button>
                ))}
              </div>}
            </>
          )}
          {/* eslint-disable-next-line jsx-a11y/interactive-supports-focus */}
          <div
            ref={menuRef}
            id={menuId}
            role="menu"
            aria-label={hasLenses ? t('nav:lens.menuLabel') : t('nav:lens.surfaces.title')}
            className="lens-menu"
            onKeyDown={onMenuKeyDown}
          >
            {hasLenses && (
              <>
                {listed.map((l) => row(l, l.kind === 'role' && l.composes.length ? l.composes.join(' + ') : l.blurb))}
                <div className="lens-menu-divider" role="separator" />
                {row(EVERYTHING, t('nav:lens.blurbs.all'))}
              </>
            )}
            {defaultOptions.length >= 2 && (
              <div role="group" aria-labelledby={`${menuId}-default-surface`} className="lens-surfaces">
                <div className="lens-surfaces-head" id={`${menuId}-default-surface`}>
                  <span>{t('nav:lens.defaultSurface.title')}</span>
                  <span className="lens-surfaces-sub">{t('nav:lens.defaultSurface.subtitle')}</span>
                </div>
                {defaultOptions.map((o) => {
                  const on = o.id === defaultSurfaceId;
                  return (
                    <button
                      key={o.id}
                      type="button"
                      role="menuitemcheckbox"
                      aria-checked={on}
                      tabIndex={-1}
                      className={`lens-surface-item${on ? ' is-checked' : ''}`}
                      onClick={() => chooseDefaultSurface(o.id)}
                    >
                      <span className="lens-surface-label">{o.label}</span>
                      <span className="lens-surface-switch" aria-hidden="true">
                        <span className="lens-surface-knob" />
                      </span>
                    </button>
                  );
                })}
                {defaultChosen && (
                  <button
                    type="button"
                    role="menuitem"
                    tabIndex={-1}
                    className="lens-surfaces-reset"
                    onClick={() => chooseDefaultSurface(defaultChosen.id)}
                  >
                    {t('nav:lens.defaultSurface.clear')}
                  </button>
                )}
              </div>
            )}
            {hasSurfaces && (
              <div role="group" aria-labelledby={`${menuId}-surfaces`} className="lens-surfaces">
                <div className="lens-surfaces-head" id={`${menuId}-surfaces`}>
                  <span>{t('nav:lens.surfaces.title')}</span>
                  <span className="lens-surfaces-sub">{t('nav:lens.surfaces.subtitle')}</span>
                </div>
                {surfaceList.map((sf) => {
                  const last = sf.shown && shownCount <= 1;
                  return (
                    <button
                      key={sf.key}
                      type="button"
                      role="menuitemcheckbox"
                      aria-checked={sf.shown}
                      aria-disabled={last || undefined}
                      title={last ? t('nav:lens.surfaces.lastOne') : undefined}
                      tabIndex={-1}
                      className={`lens-surface-item${sf.shown ? ' is-checked' : ''}${last ? ' is-last' : ''}`}
                      onClick={() => { if (!last) surfaceState.toggle(sf.key); }}
                    >
                      <span className="lens-surface-label">{sf.labelKey ? t(sf.labelKey, { defaultValue: sf.label }) : sf.label}</span>
                      {!sf.licensed && <NavLock className="lens-surface-lock" module={sf.labelKey ? t(sf.labelKey, { defaultValue: sf.label }) : sf.label} />}
                      {/* A toggle, read by assistive tech as the item's checked state. */}
                      <span className="lens-surface-switch" aria-hidden="true">
                        <span className="lens-surface-knob" />
                      </span>
                    </button>
                  );
                })}
                {surfaceState.customised && (
                  <button
                    type="button"
                    role="menuitem"
                    tabIndex={-1}
                    className="lens-surfaces-reset"
                    onClick={() => surfaceState.reset()}
                  >
                    {t('nav:lens.surfaces.reset')}
                  </button>
                )}
              </div>
            )}
          </div>
          {hasLenses && <p className="lens-menu-footnote">{t('nav:lens.footnote')}</p>}
        </div>
      )}
    </div>
  );
};

export default LensSwitcher;
